import { SelectionModel } from '@angular/cdk/collections';
import { MatTreeFlatDataSource } from '@angular/material/tree';
import { AppHelper } from '@app/core/app.helper';
import { INode } from '../interfaces/INode';
import { InterfaceTreeClassService } from '../interfaces/InterfaceTreeClassService';
import { ITreeConfig } from '../interfaces/ITreeConfig';
/**
 * Интерфейс класс компонентов дерева в приложении
 * @package Tree
 * @version 1.0
 * @author BOLOTOV I.P.
 * @since 31.08.2022
 * @copyright (c) Internet-Frigate {@link http://www.ifrigate.ru}
 */
export abstract class TreeServiceClass implements InterfaceTreeClassService {
    /** Данные дерева */
    treeData: INode[];
    /** Выбранная запись */
    selectNode: INode;
    /** Конфиг компонента */
    config: ITreeConfig;
    /** Данные дерева */
    dataSource: MatTreeFlatDataSource<INode, INode>;
    /** Узел, на котором установлен фокус */
    focusedNode: INode;
    /** Модель данных, содержащая id выбранных (для множественного выбора) узлов */
    checklistSelection: SelectionModel<string> = new SelectionModel<string>(true);
    /** Количество найденных записей */
    filterResult: INode[];
    /** Индекс текущей найденной записи */
    filterResultIndex: number = -1;

    /** Генерация данных для дерева */
    prepareTreeData() {
        let parentId: string | number;

        if (this.config.hasOwnProperty('parentId') && this.config.parentId) {
            parentId = this.config.parentId;
        } else {
            parentId = this.searchParentId(this.config.listNodes);
            this.config.parentId = parentId;
        }

        if (
            !this.config.sortNameLevel &&
            typeof this.config.listNodes[0] === 'object' &&
            this.config.listNodes[0].hasOwnProperty('p_sort') &&
            this.config.listNodes[0].p_sort == '1'
        ) {
            this.config.sortNameLevel = [1];
        }

        this.treeData = this.generateTreeData(this.config.listNodes, parentId, 1, null, this.config.sortNameLevel);

        this.dataSource.data = this.treeData;
    }

    /**
     * Поиск корня для дерева среди данных дерева
     * @param data - плоский список элементов дерева
     * @private
     */
    private searchParentId(data: any[]): string | number | null {
        let id = null;
        data.forEach((item) => {
            if (item.hasOwnProperty('parent_id')) {
                const parentId = item?.parent_id;
                if (id == null || Number(parentId) < Number(id)) {
                    id = parentId;
                }
            }
        });

        if (id === null) {
            const parentNde = data.filter((value) => !value.parent_id);
            if (parentNde) {
                id = parentNde[0]?.id;
            }
        }
        // Все списки в основном идут из бд и id родителя это по итогу строка
        return String(id);
    }

    /**
     * Генерация данных для дерева
     * @param data
     * @param parentId
     * @param level
     * @param parentNode
     * @param sortLevelName
     * @private
     */
    generateTreeData(
        data: INode[],
        parentId: number | string,
        level: number,
        parentNode: INode,
        sortLevelName: number[] = [],
    ): INode[] {
        const result: INode[] = [];
        data.forEach((node) => {
            if (node.parent_id == parentId) {
                const newNode: INode = Object.assign({}, node);
                newNode.level = level;
                newNode.children = this.generateTreeData(data, node.id, level + 1, newNode, sortLevelName);
                newNode.is_children = node?.children?.length !== 0;

                newNode.is_folder = newNode.is_folder == '1';

                if (this.config?.valueData && this.config?.valueData.length) {
                    let index = -1;
                    if (Array.isArray(this.config?.valueData)) {
                        index = this.config?.valueData.findIndex((item) => item == newNode.id);
                    } else {
                        index = this.config?.valueData == newNode.id ? 1 : -1;
                    }
                    if (index > -1) {
                        this.checklistSelection.select(String(newNode.id));
                    }
                } else if (newNode?.selected) {
                    this.checklistSelection.select(String(newNode.id));
                }

                if (newNode.is_folder) {
                    if (newNode?.need_sort == '1') {
                        newNode.children.sort(AppHelper.compare({ is_folder: 1, name: -1 }));
                    }

                    if (!(newNode?.children.length === 0 && this.config?.isHideEmptyFolder)) {
                        result.push(newNode);
                    }
                } else {
                    result.push(newNode);
                }
            }
        });
        // Сортировка по имени для конкретного уровня дерева
        if (sortLevelName.length > 0 && sortLevelName.includes(level)) {
            result.sort(AppHelper.compare({ is_folder: 1, name: -1 }));
        }
        return result;
    }

    /**
     * Поиск по данным дерева
     * @param data - данные в которых будем искать
     * @param search - искомая строка
     */
    searchTreeData(data: INode[], search: RegExp): INode[] {
        const result: INode[] = [];
        // Поиск
        data.forEach((value) => {
            let children = [];
            let node;
            if (value.children && value.children.length > 0) {
                children = this.searchTreeData(value.children, search);
            }
            if (children.length > 0 || String(value.name).match(search)) {
                node = Object.assign({}, value);
                node.children = children;
                result.push(node);
            }
        });
        return result;
    }

    /**
     * Поиск по данным дерева и возвращение плоского массива с найденными ветвями
     * @param data - данные в которых будем искать
     * @param search - искомая строка
     * @param result - результат поиска
     */
    searchTreeDataFlat(data: INode[], search: RegExp, result: INode[]): INode[] {
        // Поиск
        data.forEach((value) => {
            if (value.children && value.children.length > 0) {
                result.concat(this.searchTreeDataFlat(value.children, search, result));
            }
            if (String(value.name).match(search)) {
                result.push(value);
            }
        });
        return result;
    }

    /**
     * Метод для конвертации значения условно удаленного рубрикатора
     * @param node - узел (рубрикатор)
     * @return boolean
     */
    convertTypeRubricatorFlagDel(node): boolean {
        if (node && node.hasOwnProperty('del')) {
            return node.del === true || node.del === '1';
        }
        if (node && node.hasOwnProperty('is_del')) {
            return node.is_del === true || node.is_del === '1';
        }
        return false;
    }

    /**
     * Рекурсивный поиск записи
     * @param arr данные для поиска
     * @param id ид записи
     */
    findNode(arr: any[], id: string | number): any {
        let result = null;
        let subResult;
        arr.forEach((item) => {
            if (item.id == id) {
                result = item;
            } else if (item.children) {
                subResult = this.findNode(item.children, id);
                if (subResult) {
                    result = subResult;
                }
            }
        });
        return result;
    }
}
